import React, { useEffect, useMemo, useState } from "react";
// ANT DESIGN COMPONENTS
import { Spin, Divider, Input } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuildingCircleCheck, faGears, faUser } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { usePostUserMutation, usePutUserMutation } from "../../../redux/users/userAPI";
import { useGetOrganizationsLightQuery } from "../../../redux/organizations/organizationAPI";
import { updateOrganizationAndChilds } from "../../../redux/organizations/organizationSlice";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// COMPONENTS
import OrganizationSelectOrganizationSubOrganization from "../../organizationManagement/organizations/OrganizationSelectOrganizationSubOrganization";
import FormInputText from "../../components/form/FormInputText";
import FormInputPhoneNumber from "../../components/form/FormInputPhoneNumber";
import ActionFiringModeSelect from "../../../components/actionFiringMode/ActionFiringModeSelect";
import FormInputSelectRole from "../../components/form/FormInputSelectRole";
import GlobalAlert2 from "../../home/GlobalAlert2";
import ButtonClose from "../../components/buttons/ButtonClose";
import ButtonSave from "../../components/buttons/ButtonSave";
// INTERFACES
import { UserInterface } from "../../../interfaces/User.interface";
import { RootState } from "../../../app/store";
import { FormAction } from "../../../types/enums";
import { FormularyType } from "../../../interfaces/Wizard.interface";
import { formatUuidT } from "../../../flatbuffers/display";

interface UsersCreateEditFormProps {
  mode?: FormAction;
  userId?: string;
  initialUser?: any;
  onCancel?: () => void;
  onSaveSuccess?: () => void;
}

function UsersCreateEditForm({
  mode = FormAction.Create,
  userId = "",
  initialUser = null,
  onCancel = () => {},
  onSaveSuccess = () => {},
}: UsersCreateEditFormProps) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");
  const BLANK_USER = {
    firstName: "",
    middleName: "",
    lastName: "",
    roleId: "",
    organizationId: "",
    email: "",
    contactInfo: [
      {
        id: "",
        address: "",
        phone: "1",
        phone_code: "",
        priority: 0,
      },
    ],
  };

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [user, setUser] = useState<UserInterface>(BLANK_USER);
  const [organizationsAndChildsIsLoading, setOrganizationsAndChildsIsLoading] = useState(false);
  const [tokenOrganizations, setTokenOrganizations] = useState<string | null>(null);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { userLogged } = useSelector((state: RootState) => state.home);
  const { organizationAndChilds } = useSelector((state: RootState) => state.organization);
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerPostUser,
    {
      isSuccess: isSuccessPostUser,
      isLoading: isLoadingPostUser,
      isError: isErrorPostUser,
      error: errorPostUser,
      reset: resetPostUser,
    },
  ] = usePostUserMutation();
  const [
    triggerPutUser,
    {
      isSuccess: isSuccessPutUser,
      isError: isErrorPutUser,
      error: errorPutUser,
      isLoading: isLoadingPutUser,
      reset: resetPutUser,
    },
  ] = usePutUserMutation();
  const { data: dataOrganizationsLight } = useGetOrganizationsLightQuery(
    { token: tokenOrganizations },
    { skip: !tokenOrganizations, refetchOnMountOrArgChange: false },
  );

  const organizationNameById = useMemo(() => {
    const result: Record<string, string> = {};
    if (!dataOrganizationsLight?.data) return result;

    dataOrganizationsLight.data.forEach((organization: any) => {
      const id = formatUuidT(organization?.id);
      const name = String(organization?.name ?? "").trim();
      if (id && name) {
        result[id] = name;
      }
    });

    return result;
  }, [dataOrganizationsLight]);

  const organizationDisplayName =
    organizationNameById[user.organizationId] ||
    String(initialUser?.orgName ?? initialUser?.org?.name ?? "").trim() ||
    user.organizationId;

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const cleanOrganizationsList = () => {
    dispatch(
      updateOrganizationAndChilds({
        childs: [],
      }),
    );
  };

  const isCreateMode = mode === FormAction.Create;

  const normalizeId = (value: unknown): string => {
    if (value == null) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") return formatUuidT(value as any) || "";
    return String(value);
  };

  const mapUserToForm = (sourceUser: any): UserInterface => {
    const contactInfoItem = sourceUser?.contactInfo?.[0] ||
      sourceUser?.contact_info?.[0] || {
        id: "",
        address: "",
        phone: "1",
        phone_code: "",
        priority: 0,
      };

    return {
      firstName: sourceUser?.firstName ?? sourceUser?.first_name ?? "",
      middleName: sourceUser?.middleName ?? sourceUser?.middle_name ?? "",
      lastName: sourceUser?.lastName ?? sourceUser?.last_name ?? "",
      roleId: normalizeId(sourceUser?.roleId ?? sourceUser?.role_id ?? sourceUser?.role?.id),
      organizationId: normalizeId(
        sourceUser?.organizationId ??
          sourceUser?.organization_id ??
          sourceUser?.orgId ??
          sourceUser?.org_id ??
          sourceUser?.org?.id,
      ),
      email: String(sourceUser?.email ?? "").toLowerCase(),
      contactInfo: [
        {
          id: normalizeId(contactInfoItem?.id),
          address: String(contactInfoItem?.address ?? ""),
          phone: String(contactInfoItem?.phone ?? "1"),
          phone_code: String(contactInfoItem?.phone_code ?? ""),
          priority: Number(contactInfoItem?.priority ?? 0),
        },
      ],
    };
  };

  const initData = () => {
    if (isCreateMode) {
      cleanOrganizationsList();
      const copy: UserInterface = structuredClone(BLANK_USER);
      copy.organizationId = String(userLogged.org.id ?? "");
      setUser(copy);
    }
  };

  const resetFormState = () => {
    resetPostUser();
    resetPutUser();
    cleanOrganizationsList();
    setUser(BLANK_USER);
  };

  const onChangeFormValue = (newValue: string, variableName: string) => {
    const copy: UserInterface = structuredClone(user);
    copy[variableName] = variableName === "email" ? newValue.toLowerCase() : newValue;
    setUser(copy);
  };

  const onChangeFormValueContactInfo = (newValue: string, variableName: string) => {
    const copy: UserInterface = structuredClone(user);
    copy.contactInfo[0][variableName] = newValue;
    setUser(copy);
  };

  const onClickClose = () => {
    onCancel?.();
  };

  const onClickSave = async () => {
    const token = await GETJwtToken();

    if (isCreateMode) {
      triggerPostUser({
        token,
        body: {
          first_name: user.firstName,
          middle_name: user.middleName,
          last_name: user.lastName,
          role_id: user.roleId,
          org_id: user.organizationId,
          email: user.email,
          contact_info: user.contactInfo,
        },
      });
    } else {
      triggerPutUser({
        id: userId,
        body: {
          first_name: user.firstName,
          middle_name: user.middleName,
          last_name: user.lastName,
          role_id: user.roleId,
          org_id: user.organizationId,
          email: user.email,
          contact_info: user.contactInfo,
        },
        token,
      });
    }
  };

  const validateEmail = (email: string) => {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
  };

  const disableSaveButton = () => {
    if (
      user.firstName === "" ||
      user.lastName === "" ||
      user.contactInfo[0].address === "" ||
      user.contactInfo[0].phone === "" ||
      user.roleId === "" ||
      user.organizationId === "" ||
      !validateEmail(user.email)
    ) {
      return true;
    }
    return false;
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    initData();
  }, [mode]);

  useEffect(() => {
    GETJwtToken().then(setTokenOrganizations);
  }, []);

  useEffect(() => {
    if (isSuccessPostUser || isSuccessPutUser) {
      resetFormState();
      onSaveSuccess?.();
    }
  }, [isSuccessPostUser, isSuccessPutUser, isErrorPostUser, isErrorPutUser]);

  useEffect(() => {
    if (!isCreateMode) {
      return;
    }

    if (
      organizationAndChilds &&
      organizationAndChilds.data &&
      organizationAndChilds.data.idOrganizationSelected
    ) {
      setUser((prevUser) => ({
        ...prevUser,
        organizationId: organizationAndChilds.data.idOrganizationSelected,
      }));
    }
  }, [organizationAndChilds, isCreateMode]);

  useEffect(() => {
    if (!isCreateMode && initialUser) {
      setUser(mapUserToForm(initialUser));
    }
  }, [isCreateMode, initialUser]);

  // ************************************************ */
  // COMPONENT ************************************** */
  const actionButtons = (
    <div className="container generalStyles__flex mt-4">
      <div className="generalStyles__width100 generalStyles__mrFix">
        <ButtonClose onClick={onClickClose} />
      </div>
      <div className="generalStyles__width100 generalStyles__mlFix">
        <ButtonSave onClick={onClickSave} disabledButton={disableSaveButton()} isLoading={false} />
      </div>
    </div>
  );

  const formContent = (
    <>
      <Spin
        spinning={isLoadingPostUser || organizationsAndChildsIsLoading || isLoadingPutUser}
        tip={t("general.loading")}
      >
        <div>
          {/** ------------------------------------------------------------- */}
          {/** CREATE NEW USER  */}
          <div>
            <Divider orientation="left" className="generalStyles__drawerDivider">
              <h5>
                <FontAwesomeIcon
                  icon={faUser}
                  className="generalStyles__info generalStyles__mrFix"
                />
                {isCreateMode ? (
                  <> {t("userManagement.createNewUser")}</>
                ) : (
                  <> {t("userManagement.editUser")}</>
                )}
              </h5>
            </Divider>
          </div>
        </div>
        {/** ------------------------------------------------------------- */}
        {/** USER FORM  */}
        <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
          <div className="container">
            <div className="row">
              {/** COMPONENTS FORM - INPUT EMAIL */}
              <div className="col-md-6 mt-3">
                <FormInputText
                  inputValue={user.email}
                  inputLabelName="Email"
                  inputOnChange={onChangeFormValue}
                  inputVariableName="email"
                  isRequired
                  disabledInput={false}
                  formularyType={FormularyType.WIZARD}
                />
              </div>
              {/** COMPONENTS FORM - INPUT FIRST NAME */}
              <div className="col-md-6 mt-3">
                <FormInputText
                  inputValue={user.firstName}
                  inputLabelName={t("userManagement.firstName")}
                  inputOnChange={onChangeFormValue}
                  inputVariableName="firstName"
                  isRequired
                  disabledInput={false}
                  formularyType={FormularyType.WIZARD}
                />
              </div>
              {/** COMPONENTS FORM - INPUT MIDDLE NAME */}
              <div className="col-md-6 mt-3">
                <FormInputText
                  inputValue={user.middleName}
                  inputLabelName={t("userManagement.middleName")}
                  inputOnChange={onChangeFormValue}
                  inputVariableName="middleName"
                  isRequired={false}
                  disabledInput={false}
                  formularyType={FormularyType.WIZARD}
                />
              </div>
              {/** COMPONENTS FORM - INPUT LAST NAME */}
              <div className="col-md-6 mt-3">
                <FormInputText
                  inputValue={user.lastName}
                  inputLabelName={t("userManagement.lastName")}
                  inputOnChange={onChangeFormValue}
                  inputVariableName="lastName"
                  isRequired
                  disabledInput={false}
                  formularyType={FormularyType.WIZARD}
                />
              </div>
              {/** COMPONENTS FORM - INPUT ADDRESS */}
              <div className="col-md-6 mt-3">
                <FormInputText
                  inputValue={user.contactInfo[0].address}
                  inputLabelName={t("userManagement.address")}
                  inputOnChange={onChangeFormValueContactInfo}
                  inputVariableName="address"
                  isRequired
                  disabledInput={false}
                  formularyType={FormularyType.WIZARD}
                />
              </div>
              {/** COMPONENTS FORM - INPUT PHONE NUMBER */}
              <div className="col-md-6 mt-3">
                <FormInputPhoneNumber
                  inputValue={user.contactInfo[0].phone}
                  inputLabelName={t("userManagement.phone")}
                  inputOnChange={onChangeFormValueContactInfo}
                  inputVariableName="phone"
                  isRequired
                  formularyType={FormularyType.WIZARD}
                />
              </div>
            </div>
          </div>
        </div>
        {/** ---------------------------------------------------- */}
        {/** GET ORGANIZATION AND CHILDS ----------- */}
        {isCreateMode ? (
          <OrganizationSelectOrganizationSubOrganization
            setOrganizationsAndChildsIsLoading={setOrganizationsAndChildsIsLoading}
            loadOnMount
            initialOrganizationId={user.organizationId}
            initialOrganizationName={initialUser?.orgName ?? initialUser?.org?.name ?? ""}
            disableOrganizationSelection={!isCreateMode}
          />
        ) : (
          <>
            <Divider orientation="left" className="generalStyles__drawerDivider">
              <h5>
                <FontAwesomeIcon
                  icon={faBuildingCircleCheck}
                  className="generalStyles__info generalStyles__mrFix"
                />
                {t("organizationManagement.assignToOrganization")}
              </h5>
            </Divider>
            <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
              <div className="container">
                <div className="row">
                  <div className="col-md-12 mt-3">
                    <div className="generalStyles__inputFlexNameLarge">
                      <span className="drawer__text">
                        {t("organizationManagement.organization")}
                      </span>
                    </div>
                    <div className="generalStyles__width100" style={{ marginTop: "9px" }}>
                      <Input
                        className="generalStyles__input"
                        value={organizationDisplayName}
                        disabled
                        size="large"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        {/** ---------------------------------------------------- */}
        {/** ACTION FIRING MODE — edit only.
             Absent on create because it saves through its own endpoint against
             a user id that does not exist yet; a new user starts on INHERIT,
             which is the value the column defaults to anyway. It also renders
             nothing without the grant, which is separate from being able to
             edit a user at all. */}
        {!isCreateMode && user.id && (
          <>
            <Divider orientation="left" className="generalStyles__drawerDivider">
              <h5>
                <FontAwesomeIcon
                  icon={faGears}
                  className="generalStyles__info generalStyles__mrFix"
                />
                {t("general.settings")}
              </h5>
            </Divider>
            <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
              <div className="container">
                <div className="row">
                  <div className="col-md-6 mt-3">
                    <ActionFiringModeSelect
                      target={{ kind: "user", userId: String(user.id) }}
                      value={
                        // The form's user is loosely typed, so this narrows
                        // rather than casts: anything that is not a string is
                        // not a mode, and INHERIT is the right reading of a
                        // value that never arrived.
                        typeof user.actionFiringMode === "string"
                          ? user.actionFiringMode
                          : undefined
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        {/** ---------------------------------------------------- */}
        {/** ROLE */}
        <FormInputSelectRole
          orgId={user.organizationId}
          inputValue={user.roleId}
          inputOnChange={onChangeFormValue}
          inputVariableName="roleId"
        />
      </Spin>
      {actionButtons}
      {/** ---------------------------------------------------- */}
      {/** GLOBAL ALERT */}
      <GlobalAlert2
        isError={isErrorPostUser}
        isSuccess={isSuccessPostUser}
        requestType="POST"
        error={errorPostUser}
        name="User"
      />
      <GlobalAlert2
        isError={isErrorPutUser}
        isSuccess={isSuccessPutUser}
        requestType="PUT"
        error={errorPutUser}
        name="User"
      />
    </>
  );

  return <div className="mt-3">{formContent}</div>;
}

export default UsersCreateEditForm;
